import { FetchResult } from '@apollo/client';

import { MetadataError } from '@ep/error-handling';
import type { EmissionFactor } from '@esg/esg-global-types';
import {
  BaseMutationOptions,
  CreateEmissionFactorClientData,
  CreateEmissionFactorRequest,
  EmissionFactorFilters,
  GetEmissionFactorRequest,
  ReplicateMasterListEmissionFactorsToCompanyRequest,
  ResolverMutationRequestMetadata,
  ResolverQueryRequestMetadata,
  SoftDeleteEmissionFactorClientData,
  SoftDeleteEmissionFactorRequest,
  UpdateEmissionFactorClientData,
  UpdateEmissionFactorRequest,
  parseResolverResponse
} from '@esg/syntiro-api-core';

import { validateParams } from '../validators/base.validator';
import { isValidCompanyId } from '../validators/company.validator';
import { isValidGroupId } from '../validators/group.validator';
import {
  CREATE_EMISSION_FACTOR,
  REPLICATE_LATEST_EMISSION_FACTOR_TO_COMPANY,
  SOFT_DELETE_EMISSION_FACTOR,
  UPDATE_EMISSION_FACTOR
} from '../mutations/emission_factor.mutations';
import { GET_EMISSION_FACTORS_QUERY } from '../queries/emission_factor.queries';
import { generateUuidV4 } from '../../util/uuid';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { auth } from '../../lib/google/firebase';
import { apollo_client_v2 } from '../../App';

const SOURCE = 'emission_factor.service.ts';

export const getEmissionFactors = async (
  metadata: ResolverQueryRequestMetadata,
  filters?: EmissionFactorFilters
): Promise<Array<EmissionFactor>> => {
  if (!metadata.master_list) {
    validateParams(
      { metadata },
      {
        'metadata.group_id': { validate: isValidGroupId, message: 'Invalid group_id specified.' },
        'metadata.company_id': {
          validate: isValidCompanyId,
          message: 'Invalid company_id specified.'
        }
      }
    );
  }
  const input: GetEmissionFactorRequest['input'] = {
    metadata,
    filter: { deleted: false, ...filters }
  };
  return apollo_client_v2
    .query({ query: GET_EMISSION_FACTORS_QUERY, variables: { input }, fetchPolicy: 'no-cache' })
    .then((response: FetchResult) => {
      if (!response.data?.getEmissionFactors) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('getEmissionFactors'));
      }
      const result = parseResolverResponse<Array<EmissionFactor>>(response.data.getEmissionFactors);
      return result ?? [];
    })
    .catch((error: unknown) => {
      const trackingId: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error
          ? error.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getEmissionFactors'),
        { metadata, filters },
        trackingId
      );
    });
};

export const createEmissionFactor = async (
  metadata: ResolverMutationRequestMetadata,
  data: CreateEmissionFactorClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<EmissionFactor | null | undefined> => {
  const input: CreateEmissionFactorRequest['input'] = {
    metadata: { ...metadata, audit_user_email: auth.currentUser?.email ?? '' },
    data: { emission_factor: data },
    options
  };
  return apollo_client_v2
    .mutate({ mutation: CREATE_EMISSION_FACTOR, variables: { input } })
    .then((response: FetchResult) => {
      if (!response.data?.createEmissionFactor) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('createEmissionFactor'));
      }
      return parseResolverResponse<EmissionFactor>(response.data.createEmissionFactor);
    })
    .catch((error: unknown) => {
      const trackingId: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error
          ? error.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createEmissionFactor'),
        { metadata, data },
        trackingId
      );
    });
};

export const updateEmissionFactor = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateEmissionFactorClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<EmissionFactor | null | undefined> => {
  const input: UpdateEmissionFactorRequest['input'] = {
    metadata: { ...metadata, audit_user_email: auth.currentUser?.email ?? '' },
    data: { emission_factor: data },
    options
  };
  return apollo_client_v2
    .mutate({ mutation: UPDATE_EMISSION_FACTOR, variables: { input } })
    .then((response: FetchResult) => {
      if (!response.data?.updateEmissionFactor) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateEmissionFactor'));
      }
      return parseResolverResponse<EmissionFactor>(response.data.updateEmissionFactor);
    })
    .catch((error: unknown) => {
      const trackingId: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error
          ? error.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateEmissionFactor'),
        { metadata, data },
        trackingId
      );
    });
};

export const softDeleteEmissionFactor = async (
  metadata: ResolverMutationRequestMetadata,
  data: SoftDeleteEmissionFactorClientData,
  options: BaseMutationOptions = { return_data: false, create_audit_log: true }
): Promise<EmissionFactor | null | undefined> => {
  const input: SoftDeleteEmissionFactorRequest['input'] = {
    metadata: { ...metadata, audit_user_email: auth.currentUser?.email ?? '' },
    data: { emission_factor: data },
    options
  };
  return apollo_client_v2
    .mutate({ mutation: SOFT_DELETE_EMISSION_FACTOR, variables: { input } })
    .then((response: FetchResult) => {
      if (!response.data?.softDeleteEmissionFactor) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('softDeleteEmissionFactor'));
      }
      return parseResolverResponse<EmissionFactor>(response.data.softDeleteEmissionFactor);
    })
    .catch((error: unknown) => {
      const trackingId: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error
          ? error.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'softDeleteEmissionFactor'),
        { metadata, data },
        trackingId
      );
    });
};

export const replicateMasterListEmissionFactorsToCompany = async (
  metadata: ResolverMutationRequestMetadata
): Promise<Array<string> | null> => {
  validateParams(
    { metadata },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      }
    }
  );

  const input: ReplicateMasterListEmissionFactorsToCompanyRequest['input'] = {
    metadata: {
      ...metadata
    }
  };
  return apollo_client_v2
    .mutate({
      mutation: REPLICATE_LATEST_EMISSION_FACTOR_TO_COMPANY,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.replicateMasterListEmissionFactorsToCompany) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('replicateMasterListEmissionFactorsToCompany'));
      }
      return response.data.replicateMasterListEmissionFactorsToCompany.result;
    })
    .catch((error: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        error instanceof Error
          ? error.message
          : FUNCTION_UNKNOWN_FAILURE('replicateMasterListEmissionFactorsToCompany'),
        { metadata },
        tracking_id
      );
    });
};
